import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  RefreshCw,
  ShieldCheck,
  ShoppingBag,
  Truck,
} from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageWrapper from "../components/common/PageWrapper";
import { ProductCard } from "../components/common/ProductCard";
import WishlistButton from "./common/WishlistButton";
import { apiRequest } from "../services/api";
import { imagesForColor, matchingVariant, priceSummary } from "../utils/storefrontVariants";
import { storefrontProductTitle } from '../utils/storefrontProductTitle';

const FREE_DELIVERY_THRESHOLD = 4999;

const getImageUrl = (image) => {
  if (!image || typeof image !== "string") return null;

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${image.startsWith("/") ? "" : "/"}${image}`;
};

// Storefront prices are sourced from the lot selected by the API. Do not fall
// back to the catalogue selling price because it may belong to an older lot.
const getVariantPrice = (variant) =>
  Number(variant?.onlinePrice ?? variant?.mrp ?? 0);

const getUniqueOptions = (variants, key) => {
  const map = new Map();

  variants.forEach((variant) => {
    const value = variant[key];

    if (value?.id && !map.has(value.id)) {
      map.set(value.id, value);
    }
  });

  return [...map.values()];
};

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

export default function ProductDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedColor = searchParams.get('color');

  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeImage, setActiveImage] = useState(0);
  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [selectedColorId, setSelectedColorId] = useState("");
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        setError("");
        setActiveImage(0);
        setSelectedSizeId("");
        setSelectedColorId("");
        setQuantity(1);

        const response = await apiRequest(`/storefront/products/${encodeURIComponent(id)}`);

        setProduct(response?.product || response);
        setSimilar(response?.similar || []);
        const loadedProduct = response?.product || response;
        if (loadedProduct?.slug && loadedProduct.slug !== id)
          navigate("/product/" + encodeURIComponent(loadedProduct.slug), {replace: true});
      } catch (requestError) {
        setError(
          requestError.message ||
            "This product is unavailable or could not be loaded.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [id, navigate]);

  const variants = useMemo(() => product?.variants || [], [product]);

  const sizeOptions = useMemo(
    () => getUniqueOptions(variants, "size"),
    [variants],
  );

  const colorOptions = useMemo(
    () => getUniqueOptions(variants, "color"),
    [variants],
  );

  useEffect(() => {
    const requested = variants.find(v => v.color?.id === requestedColor && v.isInStock);
    const initial = requested || variants.find(v => v.isInStock && v.color?.id);
    setSelectedColorId(initial?.color?.id || '');
    setSelectedSizeId('');
  }, [variants, requestedColor]);

  const selectedVariant = useMemo(() => {
    return matchingVariant(variants, selectedSizeId, selectedColorId);
  }, [variants, selectedSizeId, selectedColorId]);

  const isSelectedVariantInStock = Boolean(selectedVariant?.isInStock);
  const selectionComplete = (!sizeOptions.length || !!selectedSizeId) && (!colorOptions.length || !!selectedColorId);
  const displaySummary = priceSummary(variants.filter(v => !selectedColorId || v.color?.id === selectedColorId));

  const maxQuantity = Math.max(
    1,
    Math.floor(Number(selectedVariant?.availableQuantity || 1)),
  );

  const isSizeAvailable = (sizeId) =>
    variants.some(
      (variant) =>
        variant.isInStock &&
        variant.size?.id === sizeId &&
        (!selectedColorId || variant.color?.id === selectedColorId),
    );

  const isColorAvailable = (colorId) =>
    variants.some(
      (variant) =>
        variant.isInStock &&
        variant.color?.id === colorId,
    );

  const price = selectionComplete ? getVariantPrice(selectedVariant) : displaySummary.price;
  const mrp = Number((selectionComplete ? selectedVariant : displaySummary.variant)?.mrp ?? price);

  const discountPercent =
    mrp > price && price > 0 ? Math.round(((mrp - price) / mrp) * 100) : 0;

  const images = useMemo(() => {
    return [...imagesForColor(product?.images || [], variants, selectedColorId)]
      .sort((first, second) => {
        const firstIsThumbnail = first.role === "THUMBNAIL" ? 0 : 1;
        const secondIsThumbnail = second.role === "THUMBNAIL" ? 0 : 1;

        if (firstIsThumbnail !== secondIsThumbnail) {
          return firstIsThumbnail - secondIsThumbnail;
        }

        return Number(first.sortOrder || 0) - Number(second.sortOrder || 0);
      })
      .map((image) => ({
        src: getImageUrl(image.storageKey || image.url),
        alt: image.altText || product?.name || "Product image",
      }))
      .filter((image) => image.src);
  }, [product, variants, selectedColorId]);

  useEffect(() => { setActiveImage(0); setQuantity(1); }, [selectedColorId, selectedSizeId]);

  const activeImageSource = images[activeImage]?.src;

  const selectedSize = sizeOptions.find((size) => size.id === selectedSizeId);

  const selectedColor = colorOptions.find(
    (color) => color.id === selectedColorId,
  );

  const showPreviousImage = () => {
    if (images.length < 2) return;

    setActiveImage((current) =>
      current === 0 ? images.length - 1 : current - 1,
    );
  };

  const showNextImage = () => {
    if (images.length < 2) return;

    setActiveImage((current) =>
      current === images.length - 1 ? 0 : current + 1,
    );
  };

  const handleImageSwipe = (startX, endX) => {
    const distance = startX - endX;

    if (Math.abs(distance) < 45) return;

    if (distance > 0) {
      showNextImage();
    } else {
      showPreviousImage();
    }
  };

  const handleAddToCart = (goToCart = false) => {
    if (sizeOptions.length && !selectedSizeId) {
      alert("Please select a size.");
      return;
    }

    if (colorOptions.length && !selectedColorId) {
      alert("Please select a colour.");
      return;
    }

    if (!selectedVariant) {
      alert("This product currently has no purchasable variant.");
      return;
    }

    if (!selectedVariant.isInStock) {
      alert("This selected variant is currently out of stock.");
      return;
    }

    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const existingItem = cart.find(
      (cartItem) => cartItem.variantId === selectedVariant.id,
    );

    if (existingItem) {
      const nextQuantity = Number(existingItem.quantity || 1) + quantity;

      existingItem.quantity = Math.min(nextQuantity, maxQuantity);
    } else {
      cart.push({
        id: product.id,
        slug: product.slug,
        variantId: selectedVariant.id,
        sku: selectedVariant.sku,
        title: storefrontProductTitle(product),
        productCode: product.productCode || '',
        brand: product.brand?.name || "CHARMS",
        image: activeImageSource,
        price,
        mrp,
        availableQuantity: Number(selectedVariant.availableQuantity || 0),
        size: selectedSize?.name || selectedSize?.code || "",
        color: selectedColor?.name || "",
        quantity,
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));

    if (goToCart) {
      navigate("/cart");
      return;
    }

    alert("Added to your shopping bag.");
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="mx-auto max-w-7xl animate-pulse px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-7 h-4 w-52 bg-slate-100" />

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.1fr)_390px]">
            <div className="aspect-[3/4] bg-slate-100" />
            <div className="h-[680px] bg-slate-100" />
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (error || !product) {
    return (
      <PageWrapper>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6">
          <section className="border border-dashed border-slate-300 bg-[#faf7f5] p-8 sm:p-12">
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
              Product unavailable
            </h1>

            <p className="mt-3 text-sm leading-6 text-slate-500">
              {error || "This product is no longer available online."}
            </p>

            <button
              type="button"
              onClick={() => navigate("/search/all")}
              className="mt-7 bg-primary-800 px-6 py-3 text-sm font-bold text-white transition hover:bg-primary-950"
            >
              Browse the collection
            </button>
          </section>
        </div>
      </PageWrapper>
    );
  }

  const productDescription =
    product.fullDescription ||
    product.description ||
    product.shortDescription ||
    "Product details will be updated soon.";

  return (
    <PageWrapper>
      <main className="bg-[#faf9f7]">
        <div className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-9 lg:px-8">
          {/* Breadcrumb */}
          <nav className="mb-7 flex items-center gap-2 overflow-hidden text-xs font-medium text-slate-400">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="shrink-0 transition hover:text-primary-800"
            >
              Home
            </button>

            <ChevronRight size={13} className="shrink-0" />

            {product.category ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/category/${product.category.slug || product.category.id}`,
                    )
                  }
                  className="shrink-0 capitalize transition hover:text-primary-800"
                >
                  {product.category.name}
                </button>

                <ChevronRight size={13} className="shrink-0" />
              </>
            ) : null}

            <span className="truncate text-slate-600">{storefrontProductTitle(product)}</span>
          </nav>

          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(340px,410px)] xl:grid-cols-[minmax(0,1fr)_minmax(380px,440px)]">
            {/* Product gallery */}
            <section className="min-w-0">
              <div className="grid gap-4 sm:grid-cols-[92px_minmax(0,1fr)] lg:grid-cols-[104px_minmax(0,1fr)]">
                {images.length > 1 ? (
                  <div className="order-2 flex gap-2 overflow-x-auto px-0.5 pb-2 sm:order-1 sm:max-h-[calc(100vh-150px)] sm:flex-col sm:overflow-y-auto sm:pb-0">
                    {images.map((image, index) => (
                      <button
                        key={`${image.src}-${index}`}
                        type="button"
                        onClick={() => setActiveImage(index)}
                        className={`relative aspect-[3/4] w-[72px] shrink-0 overflow-hidden bg-slate-100 transition sm:w-full ${
                          activeImage === index
                            ? "ring-2 ring-primary-800 ring-offset-2"
                            : "opacity-65 hover:opacity-100"
                        }`}
                        aria-label={`View image ${index + 1}`}
                        aria-current={
                          activeImage === index ? "true" : undefined
                        }
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                ) : null}

                <div
                  className="relative order-1 min-h-[560px] overflow-hidden bg-slate-100 sm:order-2 sm:min-h-[680px] xl:min-h-[760px]"
                  onTouchStart={(event) => {
                    event.currentTarget.dataset.touchStart =
                      event.touches[0]?.clientX || "0";
                  }}
                  onTouchEnd={(event) => {
                    const startX = Number(
                      event.currentTarget.dataset.touchStart || 0,
                    );
                    const endX = event.changedTouches[0]?.clientX || 0;

                    handleImageSwipe(startX, endX);
                  }}
                >
                  {images.length ? (
                    <>
                      <img
                        key={activeImageSource}
                        src={activeImageSource}
                        alt={images[activeImage]?.alt || product.name}
                        className="h-full w-full scale-[1.04] object-cover transition-transform duration-500"
                      />

                      {images.length > 1 ? (
                        <>
                          <button
                            type="button"
                            onClick={showPreviousImage}
                            className="absolute left-4 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-950 shadow-md transition hover:scale-105 hover:bg-white sm:flex"
                            aria-label="Previous product image"
                          >
                            <ChevronLeft size={19} />
                          </button>

                          <button
                            type="button"
                            onClick={showNextImage}
                            className="absolute right-4 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-950 shadow-md transition hover:scale-105 hover:bg-white sm:flex"
                            aria-label="Next product image"
                          >
                            <ChevronRight size={19} />
                          </button>

                          <span className="absolute bottom-4 right-4 bg-black/65 px-3 py-1.5 text-xs font-semibold tabular-nums text-white backdrop-blur-sm">
                            {activeImage + 1} / {images.length}
                          </span>
                        </>
                      ) : null}
                    </>
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary-100 via-[#f5e8eb] to-primary-200 p-10 text-center">
                      <span className="text-2xl font-semibold text-primary-900">
                        {storefrontProductTitle(product)}
                      </span>
                    </div>
                  )}

                  {product.isNewArrival ? (
                    <span className="absolute left-4 top-4 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-950 shadow-sm">
                      New arrival
                    </span>
                  ) : null}
                </div>
              </div>
            </section>

            {/* Purchase section */}
            <aside className="h-fit border border-[#ded8d4] bg-white p-6 sm:p-7 lg:sticky lg:top-32">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.19em] text-primary-800">
                    {product.brand?.name || "CHARMS"}
                  </p>

                  <h1 className="mt-2 text-3xl font-semibold leading-[1.1] tracking-[-0.04em] text-slate-950">
                    {storefrontProductTitle(product)}
                  </h1>
                  {product.productCode && <p className="mt-2 text-sm text-slate-500">Article No: {product.productCode}</p>}
                </div>

                <WishlistButton
                  variantId={selectedVariant?.id}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition hover:border-primary-800 hover:text-primary-800"
                />
              </div>

              <div className="mt-6 border-y border-slate-200 py-5">
                <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                  <span className="text-3xl font-semibold tracking-tight text-slate-950">
                    {price > 0 ? `${!selectionComplete && displaySummary.varies ? 'From ' : ''}${formatCurrency(price)}` : "Price on request"}
                  </span>

                  {mrp > price && price > 0 ? (
                    <>
                      <span className="pb-1 text-sm text-slate-400 line-through">
                        {formatCurrency(mrp)}
                      </span>

                      <span className="mb-1 bg-primary-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.11em] text-primary-800">
                        {discountPercent}% off
                      </span>
                    </>
                  ) : null}
                </div>

                <p className="mt-2 text-xs text-slate-500">
                  Inclusive of all taxes
                </p>

                <p
                  className={`mt-4 text-xs font-bold uppercase tracking-[0.12em] ${
                    isSelectedVariantInStock
                      ? "text-emerald-700"
                      : "text-red-700"
                  }`}
                >
                  {isSelectedVariantInStock
                    ? "Available online"
                    : "Currently out of stock"}
                </p>
              </div>

              {colorOptions.length ? (
                <div className="mt-6">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-950">
                      Select colour
                    </h2>

                    {selectedColor ? (
                      <span className="text-xs text-slate-500">
                        {selectedColor.name}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {colorOptions.map((color) => {
                      const isAvailable = isColorAvailable(color.id);
                      const isSelected = selectedColorId === color.id;

                      return (
                        <button
                          key={color.id}
                          type="button"
                          disabled={!isAvailable}
                          onClick={() => { setSelectedColorId(color.id); setSelectedSizeId(""); }}
                          title={
                            isAvailable
                              ? color.name
                              : `${color.name} - Out of Stock`
                          }
                          className={`relative flex h-9 w-9 items-center justify-center rounded-full border transition ${
                            isSelected
                              ? "border-primary-800 ring-2 ring-primary-200"
                              : "border-slate-200 hover:scale-110"
                          } ${
                            !isAvailable ? "cursor-not-allowed opacity-30" : ""
                          }`}
                          style={{
                            backgroundColor: color.hexCode || "#d1d5db",
                          }}
                          aria-label={`Select ${color.name}`}
                        >
                          {!isAvailable ? (
                            <span className="h-px w-7 rotate-45 bg-slate-700" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {sizeOptions.length ? (
                <div className="mt-6">
                  <div className="mb-3 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-slate-950">
                      Select size
                    </h2>

                    {selectedSize ? (
                      <span className="text-xs text-slate-500">
                        {selectedSize.name || selectedSize.code}
                      </span>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {sizeOptions.map((size) => {
                      const isAvailable = isSizeAvailable(size.id);
                      const isSelected = selectedSizeId === size.id;

                      return (
                        <button
                          key={size.id}
                          type="button"
                          disabled={!isAvailable}
                          onClick={() =>
                            setSelectedSizeId((current) =>
                              current === size.id ? "" : size.id,
                            )
                          }
                          className={`h-10 min-w-10 border px-3 text-xs font-bold transition ${
                            isSelected
                              ? "border-primary-800 bg-primary-800 text-white"
                              : "border-slate-300 bg-white text-slate-700 hover:border-primary-800 hover:text-primary-800"
                          } ${
                            !isAvailable
                              ? "cursor-not-allowed opacity-35 line-through hover:border-slate-300 hover:text-slate-700"
                              : ""
                          }`}
                        >
                          {size.name || size.code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="mt-7 flex items-center justify-between border-y border-slate-200 py-5">
                <p className="text-sm font-semibold text-slate-950">Quantity</p>

                <div className="flex h-10 items-center border border-slate-300">
                  <button
                    type="button"
                    disabled={!isSelectedVariantInStock || quantity <= 1}
                    onClick={() =>
                      setQuantity((current) => Math.max(1, current - 1))
                    }
                    className="flex h-full w-10 items-center justify-center text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label="Decrease quantity"
                  >
                    <Minus size={15} />
                  </button>

                  <span className="flex h-full min-w-10 items-center justify-center border-x border-slate-300 px-2 text-sm font-semibold text-slate-950">
                    {quantity}
                  </span>

                  <button
                    type="button"
                    disabled={
                      !isSelectedVariantInStock || quantity >= maxQuantity
                    }
                    onClick={() =>
                      setQuantity((current) =>
                        Math.min(maxQuantity, current + 1),
                      )
                    }
                    className="flex h-full w-10 items-center justify-center text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-35"
                    aria-label="Increase quantity"
                  >
                    <Plus size={15} />
                  </button>
                </div>
              </div>

              <div className="mt-6 grid gap-3">
                <button
                  type="button"
                  disabled={!isSelectedVariantInStock || !selectionComplete}
                  onClick={() => handleAddToCart(false)}
                  className="flex w-full items-center justify-center gap-2 bg-primary-800 py-4 text-sm font-bold text-white transition hover:bg-primary-950 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                >
                  <ShoppingBag size={17} />
                  {!selectionComplete ? "Select colour and size" : isSelectedVariantInStock ? "Add to bag" : "Out of stock"}
                </button>

                <button
                  type="button"
                  disabled={!isSelectedVariantInStock || !selectionComplete}
                  onClick={() => handleAddToCart(true)}
                  className="border border-slate-900 py-4 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                >
                  Buy now
                </button>
              </div>

              <div className="mt-6 space-y-3 border-t border-slate-200 pt-5">
                <Benefit
                  icon={Truck}
                  text={`Free delivery above ${formatCurrency(
                    FREE_DELIVERY_THRESHOLD,
                  )}`}
                />
                <Benefit icon={RefreshCw} text="Easy returns assistance" />
                <Benefit icon={ShieldCheck} text="100% genuine products" />
              </div>
            </aside>
          </div>

          {/* Product information */}
          <section className="mt-12 grid gap-8 border-t border-[#eadde1] pt-10 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-800">
                Product details
              </p>

              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                Made for your wardrobe.
              </h2>

              <p className="mt-5 max-w-3xl whitespace-pre-line text-sm leading-7 text-slate-600 sm:text-base">
                {productDescription}
              </p>
            </div>

            <div className="border-l-2 border-primary-800 pl-5">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                Selected variant
              </p>

              <div className="mt-4 space-y-2 text-sm">
                {selectedVariant?.sku ? (
                  <p className="text-slate-500">
                    SKU:{" "}
                    <span className="font-medium text-slate-950">
                      {selectedVariant.sku}
                    </span>
                  </p>
                ) : null}

                {selectedColor ? (
                  <p className="text-slate-500">
                    Colour:{" "}
                    <span className="font-medium text-slate-950">
                      {selectedColor.name}
                    </span>
                  </p>
                ) : null}

                {selectedSize ? (
                  <p className="text-slate-500">
                    Size:{" "}
                    <span className="font-medium text-slate-950">
                      {selectedSize.name || selectedSize.code}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </section>

          {/* Similar products */}
          {similar.length ? (
            <section className="mt-14 border-t border-[#eadde1] pt-10">
              <div className="mb-7 flex items-end justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-800">
                    You may also like
                  </p>

                  <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                    More from the collection
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => navigate("/search/all")}
                  className="hidden text-xs font-bold uppercase tracking-[0.12em] text-primary-800 transition hover:text-primary-950 sm:block"
                >
                  View all
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5">
                {similar.slice(0, 3).map((similarProduct, index) => (
                  <ProductCard
                    key={similarProduct.id}
                    item={similarProduct}
                    index={index}
                    navigate={navigate}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </main>
    </PageWrapper>
  );
}

function Benefit({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-xs text-slate-600">
      <Icon size={16} className="shrink-0 text-primary-800" />
      <span>{text}</span>
    </div>
  );
}
